import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Building2, 
  Package, 
  Activity,
  Calendar,
  Download,
  BarChart3,
  PieChart,
  LineChart,
  Globe,
  Eye,
  Clock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HubSwitcher from '@/components/admin/HubSwitcher';

// Import the advanced analytics components
import PackageAnalyticsChart from '@/components/analytics/PackageAnalyticsChart';
import TenderAnalyticsSummary from '@/components/analytics/TenderAnalyticsSummary';
import TrafficInsights from '@/components/analytics/TrafficInsights';


interface AnalyticsData {
  totalHubs: number;
  totalUsers: number;
  totalProjects: number;
  totalPackages: number;
  monthlyActiveUsers: number;
  monthlyRevenue: number;
  growthRate: number;
  topHubs: Array<{
    name: string;
    users: number;
    projects: number;
    packages: number;
    revenue: number;
  }>;
  userActivity: Array<{
    date: string;
    activeUsers: number;
    newUsers: number;
    logins: number;
  }>;
  packageStats: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
}

const mockAnalyticsData: AnalyticsData = {
  totalHubs: 5,
  totalUsers: 98,
  totalProjects: 54,
  totalPackages: 127,
  monthlyActiveUsers: 67,
  monthlyRevenue: 45200,
  growthRate: 12.5,
  topHubs: [
    {
      name: 'HG Construction Hub',
      users: 25,
      projects: 12,
      packages: 45,
      revenue: 18000
    },
    {
      name: 'Urban Projects Hub',
      users: 32,
      projects: 20,
      packages: 38,
      revenue: 15000
    },
    {
      name: 'Argent Development Hub',
      users: 15,
      projects: 8,
      packages: 22,
      revenue: 8000
    },
    {
      name: 'Metro Construction Hub',
      users: 18,
      projects: 10,
      packages: 15,
      revenue: 6000
    },
    {
      name: 'City Build Hub',
      users: 8,
      projects: 4,
      packages: 7,
      revenue: 2400
    }
  ],
  userActivity: [
    { date: '2024-08-07', activeUsers: 45, newUsers: 3, logins: 89 },
    { date: '2024-08-08', activeUsers: 52, newUsers: 2, logins: 102 },
    { date: '2024-08-09', activeUsers: 48, newUsers: 5, logins: 95 },
    { date: '2024-08-10', activeUsers: 61, newUsers: 1, logins: 118 },
    { date: '2024-08-11', activeUsers: 58, newUsers: 4, logins: 112 },
    { date: '2024-08-12', activeUsers: 67, newUsers: 2, logins: 134 },
    { date: '2024-08-13', activeUsers: 63, newUsers: 3, logins: 127 }
  ],
  packageStats: [
    { type: 'Draft', count: 45, percentage: 35.4 },
    { type: 'Sent', count: 38, percentage: 29.9 },
    { type: 'Viewed', count: 25, percentage: 19.7 },
    { type: 'Downloaded', count: 15, percentage: 11.8 },
    { type: 'Returned', count: 4, percentage: 3.2 }
  ]
};

const AdminAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsData>(mockAnalyticsData);

  const getTrendIcon = (trend: 'up' | 'down') => {
    return trend === 'up' ? (
      <TrendingUp className="w-4 h-4 text-green-600" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-600" />
    );
  };

  const getTrendColor = (trend: 'up' | 'down') => {
    return trend === 'up' ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive insights into your platform performance</p>
        </div>
        <div className="flex items-center space-x-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          <HubSwitcher 
            onHubChange={setSelectedHubId}
            selectedHubId={selectedHubId}
          />
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Hubs</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalHubs}</div>
            <p className="text-xs text-muted-foreground">
              +{data.growthRate}% from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.monthlyActiveUsers}</div>
            <p className="text-xs text-muted-foreground">
              +12% from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalProjects}</div>
            <p className="text-xs text-muted-foreground">
              +8% from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${data.monthlyRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +{data.growthRate}% from last month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="packages">Package Analytics</TabsTrigger>
          <TabsTrigger value="tender">Tender Analytics</TabsTrigger>
          <TabsTrigger value="traffic">Traffic Insights</TabsTrigger>
          
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Activity Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="w-5 h-5 mr-2" />
                  User Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.userActivity.slice(-7).map((activity, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {new Date(activity.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm font-medium">{activity.activeUsers}</div>
                          <div className="text-xs text-gray-500">Active</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{activity.newUsers}</div>
                          <div className="text-xs text-gray-500">New</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{activity.logins}</div>
                          <div className="text-xs text-gray-500">Logins</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Hubs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="w-5 h-5 mr-2" />
                  Top Performing Hubs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.topHubs.slice(0, 5).map((hub, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                        </div>
                        <div>
                          <div className="font-medium">{hub.name}</div>
                          <div className="text-sm text-gray-500">
                            {hub.users} users • {hub.projects} projects
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">${hub.revenue.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">Revenue</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Package Analytics Tab */}
        <TabsContent value="packages" className="space-y-6">
          <PackageAnalyticsChart />
        </TabsContent>

        {/* Tender Analytics Tab */}
        <TabsContent value="tender" className="space-y-6">
          <TenderAnalyticsSummary />
        </TabsContent>

        {/* Traffic Insights Tab */}
        <TabsContent value="traffic" className="space-y-6">
          <TrafficInsights />
        </TabsContent>


      </Tabs>
    </div>
  );
};

export default AdminAnalytics;
